import { useEffect } from 'react';

import { type QueryFunction, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';

import { Currency } from '@/constants';

interface BotOrder {
  id: string;
  mmSellsToken: Currency;
  mmBuysToken: Currency;
  rateBinanceBuy1SellsForBuys: number;
  orders: {
    ordersNumber: number;
    marginPercent: number;
    mmSellTokenAmount: number;
  }[];
  lastChangeAt: string;
}

type BotPrice = {
  [K in Currency]: number;
};

export type BotCoinPrice = Partial<{
  [key in Currency]: BotPrice;
}>;

type CoinPricesStore = {
  coinPrices: BotCoinPrice;
  error: Error | null;
  isLoading: boolean;
  isExist: boolean;
  setCoinPrices: (coinPrices: BotCoinPrice) => void;
  setError: (error: Error | null) => void;
  setIsLoading: (isLoading: boolean) => void;
};

export const useBotPrice = () => {
  const { setCoinPrices, setError, setIsLoading } = useBotPriceStore();

  const queryKey = ['botPrices'];

  const queryFn: QueryFunction<BotCoinPrice> = async () => {
    const response = await fetch(import.meta.env.VITE_BOT_RATIOS_URL);

    const orders = ((await response.json()) as { botOrders: BotOrder[] }).botOrders;

    return orders.reduce((acc, cur) => {
      acc[cur.mmBuysToken] = {
        ...(acc[cur.mmBuysToken] || ({} as BotPrice)),
        [cur.mmSellsToken]: 1 / cur.rateBinanceBuy1SellsForBuys,
      } as BotPrice;
      return acc;
    }, {} as BotCoinPrice);
  };

  const { data, error, isLoading } = useQuery({
    queryKey,
    queryFn,
    refetchOnMount: true,
    refetchInterval: (60 / 30) * 10 * 1000,
    enabled: !!import.meta.env.VITE_BOT_RATIOS_URL,
  });

  useEffect(() => {
    setIsLoading(isLoading);
    setError(error);

    if (!isLoading && !error && data) {
      setCoinPrices(data);
    }
  }, [data, isLoading, error]);
};

const useBotPriceStore = create<CoinPricesStore>((set) => {
  return {
    coinPrices: {} as BotCoinPrice,
    error: null,
    isLoading: false,
    isExist: !!import.meta.env.VITE_BOT_RATIOS_URL,
    setError: (error) => set({ error }),
    setIsLoading: (isLoading) => set({ isLoading }),
    setCoinPrices: (coinPrices) => set({ coinPrices }),
  };
});

export default useBotPriceStore;
